import { Button, Modal, ServiceError, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";

import { TOTPErrorCode, useDeleteTOTP } from "../../hooks/useTwoFactor";

import style from "./TwoFactorDisableModal.module.css";

export type TwoFactorDisableModalProps = {
  open: boolean;
  onClose: () => void;
};

type FormData = {
  code: string;
};

export default function TwoFactorDisableModal({ open, onClose }: TwoFactorDisableModalProps) {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      code: "",
    },
  });

  const { mutateAsync: mutateDeleteTOTP } = useDeleteTOTP();

  const onSubmit = async (data: FormData) => {
    try {
      await mutateDeleteTOTP({ code: data.code });
      onClose();
    } catch (error) {
      let message = "Something went wrong. Please try again.";
      if (error instanceof ServiceError) {
        const errorCode = error.serviceErrorResponse.code;
        if (errorCode === TOTPErrorCode.InvalidCode) {
          message = "Invalid code. Please try again.";
        }
      }
      setError("code", { message });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="medium"
      title="Are you sure you want to disable 2FA?"
      footer={
        <div className={style.footer}>
          <Button variant="primary" onClick={onClose}>
            Keep 2FA enabled
          </Button>
        </div>
      }
    >
      <div className={style.container}>
        <div className={style.content}>
          <div className={style.description}>
            Two-factor authentication adds an extra layer of security to your account. Without it,
            your account is more vulnerable to unauthorized access.
            <div className={style.instruction}>
              Enter the 6-digit code from your authenticator app to disable the 2FA
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TOTPCodeInput
                    onChange={field.onChange}
                    onComplete={(value) => {
                      field.onChange(value);
                      handleSubmit(onSubmit)();
                    }}
                    error={Boolean(errors.code)}
                    errorMessage={errors.code?.message}
                    disabled={isSubmitting}
                    autoFocus
                  />
                )}
              />
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
}
